import type { ComponentType } from 'react';

export type LazyComponentLoader = () => Promise<{ Component: ComponentType }>;

export interface LazyRoute {
  path: string;
  handle?: {
    authEntryRedirect?: string | ((search: string) => string);
  };
  lazy: LazyComponentLoader;
}

interface SearchFlowRouteConfig {
  basePath: string;
  landingLoader: LazyComponentLoader;
  searchLoader: LazyComponentLoader;
  loginLoader: LazyComponentLoader;
  registerLoader: LazyComponentLoader;
  forgotPasswordLoader: LazyComponentLoader;
}

export const loadLandingLoginRoute: LazyComponentLoader = async () => {
  const { LoginRoute } = await import('../../Login/Login');
  return { Component: LoginRoute };
};

export const loadLandingRegisterRoute: LazyComponentLoader = async () => {
  const { RegisterRoute } = await import('../../Register/Register');
  return { Component: RegisterRoute };
};

export const loadLandingForgotPasswordRoute: LazyComponentLoader = async () => {
  const { ForgotPasswordRoute } =
    await import('../../ForgotPassword/ForgotPassword');
  return { Component: ForgotPasswordRoute };
};

const createBaseRoutes = ({
  basePath,
  landingLoader,
  searchLoader,
}: Pick<
  SearchFlowRouteConfig,
  'basePath' | 'landingLoader' | 'searchLoader'
>): LazyRoute[] => [
  {
    path: basePath,
    lazy: landingLoader,
  },
  {
    path: `${basePath}/search`,
    lazy: searchLoader,
  },
];

export function createSearchFlowAuthRoutes({
  basePath,
  loginLoader,
  registerLoader,
  forgotPasswordLoader,
}: Pick<
  SearchFlowRouteConfig,
  'basePath' | 'loginLoader' | 'registerLoader' | 'forgotPasswordLoader'
>): LazyRoute[] {
  const authEntryRedirect = (search: string): string =>
    `${basePath}/search${search}`;

  return [
    {
      path: `${basePath}/login`,
      handle: {
        authEntryRedirect,
      },
      lazy: loginLoader,
    },
    {
      path: `${basePath}/register`,
      handle: {
        authEntryRedirect,
      },
      lazy: registerLoader,
    },
    {
      path: `${basePath}/forgot-password`,
      handle: {
        authEntryRedirect,
      },
      lazy: forgotPasswordLoader,
    },
  ];
}

export function createSearchFlowRoutes(config: SearchFlowRouteConfig): {
  publicRoutes: LazyRoute[];
  protectedRoutes: LazyRoute[];
} {
  const protectedRoutes = createBaseRoutes(config);

  return {
    publicRoutes: [...protectedRoutes, ...createSearchFlowAuthRoutes(config)],
    protectedRoutes,
  };
}
